import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    Card, 
    CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContactPerson from 'components/onboardingForms/ContactPerson';

/**
 * Generate a UUID v4
 * @returns {string} A UUID v4 string
 */
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Contact information step of venue onboarding
 */
export const ContactInformationStep = ({ 
    contacts,
    errors,
    handleContactChange
}) => {
    // Use local state to track contacts while editing in this step
    const [localContacts, setLocalContacts] = useState(contacts);
    
    // Update local contacts when parent contacts change
    useEffect(() => {
        setLocalContacts(contacts);
    }, [contacts]);
    
    // Add a new contact
    const addContact = () => {
        const newContact = {
            id: generateGuid(),
            firstName: '',
            lastName: '',
            role: '',
            email: '',
            phone: '',
            isPrimary: localContacts.length === 0 // First contact is primary
        };
        
        const updatedContacts = [...localContacts, newContact];
        setLocalContacts(updatedContacts);
        
        // Sync with parent state
        handleContactChange(null, 'replace_all', updatedContacts);
    };

    // Remove a contact
    const removeContact = (contactId) => {
        // Don't allow removing the primary contact
        const primaryContact = localContacts.find(c => c.isPrimary);
        if (primaryContact && primaryContact.id === contactId) {
            return;
        }
        
        const updatedContacts = localContacts.filter(contact => contact.id !== contactId);
        setLocalContacts(updatedContacts);
        
        // Sync with parent state
        handleContactChange(null, 'replace_all', updatedContacts);
    };

    // Handle changes to contact fields
    const handleLocalContactChange = (contactId, field, value) => {
        const updatedContacts = localContacts.map(contact => 
            contact.id === contactId ? { ...contact, [field]: value } : contact
        );
        setLocalContacts(updatedContacts);
        
        // Sync with parent state
        handleContactChange(null, 'replace_all', updatedContacts);
    };

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5">
                        Contact Information
                    </Typography>
                    
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={addContact}
                    >
                        Add Contact
                    </Button>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please provide contact information for your venue. The primary contact will be the main point of contact for bookings and inquiries.
                </Typography>
                
                {localContacts.length === 0 ? (
                    <Typography color="error" align="center" sx={{ my: 3 }}>
                        Please add at least one primary contact
                    </Typography>
                ) : (
                    localContacts.map(contact => (
                        <ContactPerson
                            key={contact.id}
                            id={contact.id}
                            firstName={contact.firstName || ''}
                            lastName={contact.lastName || ''}
                            role={contact.role || ''}
                            email={contact.email || ''}
                            phone={contact.phone || ''}
                            isPrimary={contact.isPrimary}
                            onDelete={removeContact}
                            onChange={handleLocalContactChange}
                        />
                    ))
                )}
                
                {errors.contacts && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {errors.contacts}
                    </Typography>
                )}
                
                {errors.contactsEmail && (
                    <Typography color="error" variant="body2">
                        {errors.contactsEmail}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default ContactInformationStep;